import { vm } from '@/main'
import Vue from 'vue'
import * as alertType from '@/alerts/alertType'
import * as utils from '@/utils/utils'
import { getSpeedLimitByPosition } from '@/api/here'
import { traccar } from '@/api/traccar-api'

export function convertEvents(events, isNew) {
  let filteredData = events.filter(a => {
    const currentAlertType = a.type === 'alarm' ? a.attributes.alarm : a.type
    return a.type === 'alarm' ||
      a.type === 'test' ||
      vm.$store.getters.alerts.find(a => a.notification.type === currentAlertType)
  })
  filteredData = filteredData.sort(function(a, b) {
    const diff = Date.parse(b.serverTime) - Date.parse(a.serverTime)
    if (diff === 0) {
      return b.id - a.id
    }
    return diff
  })

  filteredData.forEach(e => {
    addEventInfo(e, isNew)
  })

  return filteredData
}

export function addEventInfo(e, isNew = false, stop) {
  e.device = vm.$store.getters.devices.find(d => d.id === e.deviceId)
  e.content = getNotificationContent(e, stop)
  e.originalType = e.type
  e.type = e.type === 'alarm' ? e.attributes.alarm : e.type
  e.image = getNotificationImage(e.type)
  e.color = getNotificationColor(e.type)
  e.isNew = isNew
}

function getNotificationContent(notification, stop) {
  const device = vm.$store.getters.devices.find(d => d.id === notification.deviceId)
  if (notification.type === alertType.geofenceExit || notification.type === alertType.geofenceEnter) {
    const geofence = vm.$store.getters.geofences.find(g => g.id === notification.geofenceId)
    return geofence && geofence.name
  }
  if (notification.type === alertType.deviceOverspeed) {
    const duration = device.attributes.overspeedMinimalDuration
      ? `, ${Math.round(device.attributes.overspeedMinimalDuration / 60)} minutes`
      : ''
    return Math.round(notification.attributes.speed * 1.85200) +
      ` Km/h (Max. ${~~(notification.attributes.speedLimit * 1.852)} km/h${duration})`
  }
  if (notification.type === alertType.deviceFuelDrop) {
    return ' > ' + notification.attributes.fuelDropThreshold + '%'
  }

  if (notification.type === alertType.ignitionOff) {
    const value = stop ? ((new Date(stop.endTime) - new Date(stop.startTime)) - stop.engineHours) / 1000 : 0
    return utils.calculateTimeHHMM(value)
  }

  return ''
}

function getNotificationImage(type) {
  if (type === alertType.ignitionOn || type === alertType.ignitionOff) {
    return 'fas fa-key'
  }
  if (type === alertType.geofenceEnter || type === alertType.geofenceExit) {
    return 'fas fa-draw-polygon'
  }
  if (type === alertType.routeDeviation) {
    return 'fas fa-route'
  }
  if (type === alertType.deviceOverspeed) {
    return 'fas fa-shipping-fast'
  }
  if (type === alertType.alarmSOS) {
    return 'fas fa-exclamation-circle'
  }
  if (type === alertType.alarmPowerOn) {
    return 'fas fa-cog'
  }
  if (type === alertType.deviceFuelDrop) {
    return 'fas fa-gas-pump'
  }
  if (type === alertType.driverChanged) {
    return 'fas fa-address-card'
  }
  if (type === alertType.alarmTemperature) {
    return 'fas fa-thermometer-half'
  }
  if (type === alertType.alarmBreakdown) {
    return 'fas fa-wrench'
  }
  if (type === alertType.alarmIdle) {
    return 'fas fa-pause'
  }
  if (type === alertType.maintenance) {
    return 'fas fa-wrench'
  }
  if (type === alertType.alarmCameraAdas || alertType.adasAlarmTypes.includes(type)) {
    return 'fas fa-car-crash'
  }
  if (type === alertType.alarmCameraDms || alertType.dmsAlarmTypes.includes(type)) {
    return 'fas fa-user-slash'
  }
  return ''
}

function getNotificationColor(type) {
  if (type === alertType.ignitionOn || type === alertType.geofenceEnter ||
    type === alertType.driverChanged) {
    return 'green'
  }
  if (type === alertType.ignitionOff || type === alertType.geofenceExit ||
    type === alertType.alarmSOS || type === alertType.deviceOverspeed ||
    type === alertType.deviceFuelDrop || type === alertType.routeDeviation ||
    alertType.cameraAlarmTypes.includes(type)) {
    return 'red'
  }
  return 'black'
}

export function getMessage(event) {
  Vue.$log.debug(event.type, ' device: ', event.device)
  let result = event.device && event.device.name
  if (event.content) {
    result += ' - ' + event.content
  }
  return result
}

function timetableIncludes(event, position, timetable, timezone, timeSource) {
  if (event.attributes.alarm === 'alarmGeofenceAbsence') {
    return true
  }

  const fixTime = new Date(position.fixTime)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(fixTime)
  const datePart = type => parts.find(part => part.type === type).value
  const weekDay = datePart('weekday').toLowerCase()
  const localDateTime = `${datePart('year')}-${datePart('month')}-${datePart('day')} ` +
    `${datePart('hour')}:${datePart('minute')}:${datePart('second')}`

  const toMinutes = value => {
    const [hours, minutes] = value.split(':').map(Number)
    return hours * 60 + minutes
  }
  const eventTime = Number(datePart('hour')) * 60 + Number(datePart('minute'))
  const startTime = toMinutes(timetable.startTime)
  const endTime = toMinutes(timetable.endTime)
  const weekDayEnabled = Boolean(timetable.weekDays[weekDay])
  const insideTimeWindow = startTime < endTime
    ? eventTime > startTime && eventTime < endTime
    : eventTime < endTime || eventTime > startTime
  const accepted = weekDayEnabled && insideTimeWindow

  Vue.$log.debug('timetable event filter', {
    accepted,
    reason: !weekDayEnabled
      ? `weekday ${weekDay} is disabled`
      : insideTimeWindow ? 'inside configured time window' : 'outside configured time window',
    eventId: event.id,
    deviceId: event.deviceId,
    positionId: event.positionId,
    timeSource,
    fixTime: position.fixTime,
    utcTime: fixTime.toISOString(),
    timezone: timezone || 'browser default',
    localDateTime,
    localWeekDay: weekDay,
    startTime: timetable.startTime,
    endTime: timetable.endTime,
    enabledWeekDays: timetable.weekDays
  })

  return accepted
}

// Checks whether a single notification (from a user's alert list) accepts this
// event, applying its geofence/timetable/overspeed conditions. Returns whether
// this specific notification matches - callers should try every candidate
// notification of the right type before concluding an event has no match.
async function notificationAccepts(event, notification) {
  if (event.type === 'geofenceEnter' || event.type === 'geofenceExit') {
    if (notification.attributes.geofences && !notification.attributes.geofences.includes(event.geofenceId)) {
      Vue.$log.debug('ignore event notification', notification, event)
      return false
    }
  }

  if (notification.attributes.checkGeofences === 'onlyInside' && !event.geofenceId) {
    Vue.$log.debug('ignore only inside', notification, event)
    return false
  }

  if (notification.attributes.checkGeofences === 'onlyInside' &&
    notification.attributes.geofences && notification.attributes.geofences.length &&
    !notification.attributes.geofences.includes(event.geofenceId)) {
    Vue.$log.debug('ignore only inside, not in selected geofences', notification, event)
    return false
  }

  if (notification.attributes.checkGeofences === 'onlyOutside' && event.geofenceId) {
    Vue.$log.debug('ignore only outside', notification, event)
    return false
  }

  if (notification.attributes.timetable && !notification.attributes.timetable.allWeek) {
    const device = event.device || vm.$store.getters.devices.find(d => d.id === event.deviceId)
    let position
    let timeSource = 'position.fixTime'
    try {
      [position] = await traccar.position(event.positionId, device.id).then(r => r.data)
      if (!position || !position.fixTime) {
        throw new Error('Position response has no fixTime')
      }
      event.position = position
    } catch (error) {
      timeSource = 'event.serverTime fallback'
      position = { fixTime: event.serverTime }
      Vue.$log.warn('timetable position fetch failed, using event serverTime', {
        eventId: event.id,
        deviceId: event.deviceId,
        positionId: event.positionId,
        serverTime: event.serverTime,
        error
      })
    }
    return timetableIncludes(
      event,
      position,
      notification.attributes.timetable,
      vm.$store.getters.user.attributes.timezone,
      timeSource
    )
  }

  if (event.type === 'deviceOverspeed' && event.device.attributes.overspeedByRoad) {
    const [position] = await traccar.position(event.positionId, event.device.id).then(r => r.data)
    event.position = position
    const _aboveSpeedLimit = await aboveSpeedLimit(position, event.device)
    if (_aboveSpeedLimit) {
      event.attributes.speedLimit = _aboveSpeedLimit / 1.852
      event.content = `${Math.round(event.attributes.speed * 1.85200)} Km/h (Max. ${_aboveSpeedLimit} Km/h)`
    }
    return _aboveSpeedLimit
  }

  return true
}

export async function filterEvent(event, alerts, timings) {
  let t = performance.now()
  const lap = key => {
    if (timings) { timings[key] += performance.now() - t }
    t = performance.now()
  }
  try {
    // Traccar test notifications are synthetic and have no device assignment.
    // Let them reach the UI without applying normal per-device alert filtering.
    if (event.type === 'test') {
      Vue.$log.info('accepting test notification', event)
      return true
    }

    // A user can have several notifications of the same type (e.g. multiple
    // "geofence exit" alerts, each scoped to a different set of geofences), so
    // every matching candidate needs to be tried - not just the first one found.
    const matchingAlerts = alerts.filter(a => a &&
      a.notification.type === event.type &&
      (a.notification.type !== 'alarm' || a.notification.attributes.alarms.includes(event.attributes.alarm)) &&
      (a.notification.always || vm.$store.getters.devices.find(d =>
        (d.alerts && d.alerts.find(c => c.id === a.notification.id)) || (a.devices.includes(d)))))
    lap('match')
    if (matchingAlerts.length === 0) {
      // Log ids only: passing the reactive alerts array (devices + geofences)
      // to the logger blocked the main thread for ~11 s (ticket #200).
      Vue.$log.debug('cant find notification', event.id, event.type, event.attributes && event.attributes.alarm)
      lap('warn')
      return false
    }
    for (const alert of matchingAlerts) {
      if (await notificationAccepts(event, alert.notification)) {
        lap('accept')
        return true
      }
    }
    lap('accept')
    return false
  } catch (e) {
    console.error(e)
  }
  return true
}

async function aboveSpeedLimit(position, device) {
  const maxSpeed = await getSpeedLimitByPosition(position)
  if (maxSpeed) {
    if (position.speed * 1.852 > maxSpeed + parseInt(device.attributes.overspeedThreshold || 0)) {
      return maxSpeed
    }
  }
  return false
}
